import { useEffect, useState } from 'react';
import { useSubdomain } from '@/hooks/useSubdomain';
import { supabase } from '@/integrations/supabase/client';
import { CoachProvider } from '@/contexts/CoachContext';
import Header from '@/components/Header';
import Hero from '@/components/Hero';
import About from '@/components/About';
import Services from '@/components/Services';
import FeaturedVideos from '@/components/FeaturedVideos';
import Packages from '@/components/Packages';
import Testimonials from '@/components/Testimonials';
import Contact from '@/components/Contact';
import Footer from '@/components/Footer';
import GoogleTagLoader from '@/components/GoogleTagLoader';
import SEOHead from '@/components/SEOHead';

interface CoachData {
  id: string;
  business_name: string;
  tagline?: string | null;
  logo_url?: string | null;
  website_subdomain?: string | null;
  youtube_channel_id: string | null;
  google_tag_id: string | null;
}

interface SectionVisibility {
  show_about: boolean;
  show_services: boolean;
  show_packages: boolean;
  show_testimonials: boolean;
  show_videos: boolean;
  show_contact: boolean;
}

const CoachWebsite = () => {
  const { coachId, isLoading: subdomainLoading, error: subdomainError } = useSubdomain();
  const [coach, setCoach] = useState<CoachData | null>(null);
  const [visibility, setVisibility] = useState<SectionVisibility>({
    show_about: true,
    show_services: true,
    show_packages: true,
    show_testimonials: true,
    show_videos: true,
    show_contact: true,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!coachId) {
      setLoading(false);
      return;
    }

    const fetchCoach = async () => {
      try {
        const { data, error: fetchError } = await supabase
          .from('coaches_public' as any)
          .select('id, business_name, tagline, logo_url, website_subdomain, youtube_channel_id, google_tag_id')
          .eq('id', coachId)
          .single() as { data: CoachData | null; error: any };

        if (fetchError) throw fetchError;
        setCoach(data);

        // Fetch visibility settings
        const { data: contentData } = await supabase
          .from('coach_website_content')
          .select('show_about, show_services, show_packages, show_testimonials, show_videos, show_contact')
          .eq('coach_id', coachId)
          .maybeSingle();

        if (contentData) {
          setVisibility({
            show_about: contentData.show_about ?? true,
            show_services: contentData.show_services ?? true,
            show_packages: contentData.show_packages ?? true,
            show_testimonials: contentData.show_testimonials ?? true,
            show_videos: contentData.show_videos ?? true,
            show_contact: contentData.show_contact ?? true,
          });
        }
      } catch (err: any) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchCoach();
  }, [coachId]);

  useEffect(() => {
    if (!coach) return;
    const url = typeof window !== 'undefined' ? `${window.location.origin}${window.location.pathname}` : '';
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-coach-jsonld', 'true');
    script.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'LocalBusiness',
      '@id': url,
      name: coach.business_name,
      description: coach.tagline || `Coaching services by ${coach.business_name}`,
      url,
      image: coach.logo_url || undefined,
    });
    document.head.appendChild(script);
    return () => {
      document.querySelectorAll('script[data-coach-jsonld]').forEach((n) => n.remove());
    };
  }, [coach]);

  if (subdomainLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (subdomainError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="w-16 h-16 bg-destructive/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-3xl">⚠️</span>
          </div>
          <h1 className="text-2xl font-semibold text-foreground mb-2">Website Unavailable</h1>
          <p className="text-muted-foreground">{subdomainError}</p>
        </div>
      </div>
    );
  }

  if (error || !coach) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-3xl">🔍</span>
          </div>
          <h1 className="text-2xl font-semibold text-foreground mb-2">Coach Not Found</h1>
          <p className="text-muted-foreground">
            The coach website you're looking for doesn't exist or has been removed.
          </p>
        </div>
      </div>
    );
  }

  // Render the coach's website using the CoachProvider for context
  const coachUrl = typeof window !== 'undefined' ? `${window.location.origin}${window.location.pathname}` : undefined;
  const seoTitle = `${coach.business_name} — Personal Coaching Website`.slice(0, 60);
  const seoDescription = (coach.tagline || `Book coaching sessions with ${coach.business_name}. Explore services, packages, and testimonials.`).slice(0, 160);

  return (
    <CoachProvider coachId={coach.id}>
      <SEOHead
        title={seoTitle}
        description={seoDescription}
        canonicalUrl={coachUrl}
        ogImage={coach.logo_url || undefined}
      />
      <GoogleTagLoader tagId={coach.google_tag_id} />
      <div className="min-h-screen bg-background">
        <Header />
        <Hero />
        {visibility.show_about && <About />}
        {visibility.show_services && <Services />}
        {visibility.show_videos && <FeaturedVideos />}
        {visibility.show_packages && <Packages />}
        {visibility.show_testimonials && <Testimonials />}
        {visibility.show_contact && <Contact />}
        <Footer />
      </div>
    </CoachProvider>
  );
};

export default CoachWebsite;
